import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getApiInstance } from "@/utils/api";

const api = getApiInstance();

// Helper: normalize Saudi phone numbers to E.164 (+966...)
const toE164Ksa = (input: string): string => {
  if (!input) return input;
  let s = String(input).trim();
  // remove spaces and dashes
  s = s.replace(/\s+/g, "").replace(/-/g, "");
  // handle 00 international prefix
  if (s.startsWith("00")) s = s.replace(/^00+/, "");
  // strip leading + for processing
  if (s.startsWith("+")) s = s.slice(1);
  // keep digits only
  s = s.replace(/\D/g, "");
  // drop country code if present
  if (s.startsWith("966")) s = s.slice(3);
  // drop leading 0 of local format (e.g., 05XXXXXXXX)
  if (s.startsWith("0")) s = s.slice(1);
  return `+966${s}`;
};

export type OrderItem = {
  id: number;
  price: number;
  subscription?: number | null;
  service?: number | null;
};

export type Order = {
  id: number;
  created_at: string;
  updated_at: string;
  coupon?: string;
  items: OrderItem[];
};

export type Invoice = {
  id: number;
  order: Order;
  discount_percentage: number;
  coupon_code?: string;
  cash: number;
  visa: number;
  session: number;
  points: number;
  phone?: string;
  user_info?: {
    name?: string;
    phone?: string;
  };
  // Added: transaction id from NearPay for payment receipt printing
  matching_trn?: string | null;
  is_deleted?: string;
  deleted_by_cascade?: boolean;
  created_at: string;
  updated_at: string;
};

export type PaginatedInvoicesResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: Invoice[];
};

// ❶ Get All Invoices with Pagination
export const useInvoices = (page: number = 1) => {
  return useQuery<PaginatedInvoicesResponse>({
    queryKey: ["invoices", page],
    queryFn: async () => {
      const res = await api.get(`/store/invoices/?page=${page}`);
      return res.data;
    },
    staleTime: 15 * 1000, // تقليل إلى 15 ثانية للتحديث السريع
    gcTime: 5 * 60 * 1000, // 5 دقائق
    refetchOnWindowFocus: true, // إعادة جلب عند العودة للنافذة
    refetchOnReconnect: true, // إعادة جلب عند إعادة الاتصال
    refetchOnMount: "always", // دائماً اجلب البيانات عند mount
    refetchInterval: 60 * 1000, // إعادة جلب كل دقيقة
    refetchIntervalInBackground: false, // عدم الجلب في الخلفية
  });
};

// ❶.1 Get All Invoices (للاستخدام في أماكن أخرى بدون pagination)
export const useAllInvoices = () => {
  return useQuery<Invoice[]>({
    queryKey: ["invoices", "all"],
    queryFn: async () => {
      const res = await api.get("/store/invoices/");
      return res.data.results || [];
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
  });
};

// ❂ Get Single Invoice by ID
export const useInvoice = (id: number) => {
  return useQuery<Invoice>({
    queryKey: ["invoices", id],
    queryFn: async () => {
      const res = await api.get(`/store/invoices/${id}/`);
      return res.data;
    },
    enabled: !!id,
    staleTime: 10 * 1000, // 10 ثوان فقط للفاتورة الواحدة
    gcTime: 2 * 60 * 1000, // 2 دقائق في الذاكرة
    refetchOnWindowFocus: true, // إعادة جلب عند العودة للنافذة
    refetchOnReconnect: true, // إعادة جلب عند إعادة الاتصال
    retry: 2, // إعادة المحاولة مرتين في حالة الفشل
    refetchOnMount: "always", // دائماً اجلب البيانات عند mount
  });
};

// ❃ Create New Invoice
export type CreateInvoicePayload =
  | {
      // Case 1: Order موجود من الباك اند (من صفحة الطلبات)
      order_id: number;
      cash: number;
      visa: number;
      visa_order_id: string;
    }
  | {
      // Case 2: فاتورة جديدة من الـ cart (من الـ home)
      order_items: {
        subscription?: number; // price ID for subscription
        service?: number; // price ID for service
      }[];
      coupon?: string;
      phone: string;
      cash: number;
      visa: number;
      visa_order_id: string;
    };

export const useCreateInvoice = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateInvoicePayload) => {
      // Normalize phone to +966 if present
      const normalizedPayload = ("phone" in payload && payload.phone)
        ? { ...payload, phone: toE164Ksa(payload.phone) }
        : payload;

      let sessionId = null;
      try {
        const sessionRes = await api.get("/user/sessions/");
        const sessions = sessionRes.data?.results || sessionRes.data;

        let sessionsList = [];
        if (Array.isArray(sessions)) {
          sessionsList = sessions;
        } else if (sessions?.id) {
          sessionsList = [sessions];
        }

        // البحث عن الجلسة النشطة (التي لا تحتوي على ended_at)
        const activeSession = sessionsList.find((s: any) => !s.ended_at);

        if (activeSession) {
          sessionId = activeSession.id;
        }
      } catch (error) {
        // Handle error silently
      }

      // إضافة session_id للـ payload إذا وجدت جلسة نشطة
      const finalPayload = sessionId
        ? { ...normalizedPayload, session: sessionId }
        : normalizedPayload;

      const res = await api.post("/store/invoices/", finalPayload);
      return res.data;
    },
    onSuccess: () => {
      // إنعاش جميع البيانات المرتبطة مع pagination
      client.invalidateQueries({ queryKey: ["invoices"] }); // جميع صفحات الفواتير
      client.invalidateQueries({ queryKey: ["orders"] });
      client.invalidateQueries({ queryKey: ["current-session"] });
      client.invalidateQueries({ queryKey: ["session-invoices"] }); // مهم جداً لتحديث فواتير الجلسة

      client.refetchQueries({ queryKey: ["invoices"] }); // جميع صفحات الفواتير
      client.refetchQueries({ queryKey: ["orders"] });
      client.refetchQueries({ queryKey: ["session-invoices"] });
    },
    onError: () => {
      // Handle error silently
    },
  });
};
